import { useCallback, useState } from 'react';
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DELIMITER_PATTERN = /[\s,;]+/;
const COMMIT_KEYS = new Set(['Enter', ',', ';']);
const splitIntoEmails = (input) => input
    .split(DELIMITER_PATTERN)
    .map((part) => part.trim())
    .filter(Boolean);
/**
 * Encapsulates the state and event handling for a chip/tag-style multi-email
 * input. Pressing Enter, comma, semicolon, space, or Tab finalizes the current
 * draft. Pasting a delimited string adds all recognized emails at once.
 */
export const useMultiEmailInput = ({ value, onChange, validateEmail, duplicateMessage = 'Email already added', invalidEmailMessage = 'Valid email is required', }) => {
    const [draft, setDraft] = useState('');
    const [draftError, setDraftError] = useState(undefined);
    const tryAddEmail = useCallback((raw, current) => {
        const candidate = raw.trim().toLowerCase();
        if (!candidate)
            return { added: false };
        if (current.some((existing) => existing.toLowerCase() === candidate)) {
            return { added: false, error: duplicateMessage };
        }
        if (validateEmail) {
            const error = validateEmail(candidate, current);
            if (error)
                return { added: false, error };
        }
        else if (!EMAIL_PATTERN.test(candidate)) {
            return { added: false, error: invalidEmailMessage };
        }
        return { added: true };
    }, [validateEmail, duplicateMessage, invalidEmailMessage]);
    const commitDraft = useCallback((input) => {
        const candidates = splitIntoEmails(input);
        if (candidates.length === 0) {
            setDraft('');
            setDraftError(undefined);
            return;
        }
        const accepted = [];
        const rejected = [];
        let firstError;
        let runningValue = value;
        for (const candidate of candidates) {
            const { added, error } = tryAddEmail(candidate, runningValue);
            if (added) {
                const normalized = candidate.toLowerCase();
                accepted.push(normalized);
                runningValue = [...runningValue, normalized];
            }
            else if (error) {
                firstError ??= error;
                rejected.push(candidate);
            }
        }
        if (accepted.length > 0)
            onChange([...value, ...accepted]);
        setDraft(rejected.join(' '));
        setDraftError(firstError);
    }, [onChange, tryAddEmail, value]);
    const onDraftChange = useCallback((input) => {
        setDraft(input);
        setDraftError(undefined);
    }, []);
    const onKeyDown = useCallback((event) => {
        if (COMMIT_KEYS.has(event.key)) {
            if (draft.trim().length === 0)
                return;
            event.preventDefault();
            commitDraft(draft);
        }
        else if (event.key === 'Backspace' && draft.length === 0 && value.length > 0) {
            onChange(value.slice(0, -1));
        }
    }, [commitDraft, draft, onChange, value]);
    const onBlur = useCallback(() => {
        if (draft.trim().length > 0)
            commitDraft(draft);
    }, [commitDraft, draft]);
    const onPaste = useCallback((event) => {
        const text = event.clipboardData.getData('text');
        if (DELIMITER_PATTERN.test(text)) {
            event.preventDefault();
            commitDraft(draft ? `${draft} ${text}` : text);
        }
    }, [commitDraft, draft]);
    const removeAt = useCallback((index) => {
        onChange(value.filter((_, i) => i !== index));
    }, [onChange, value]);
    return {
        draft,
        draftError,
        onDraftChange,
        onKeyDown,
        onBlur,
        onPaste,
        removeAt,
    };
};
